import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { MARKER_COLORS } from 'src/app/helpers/project-map-helper';
import { UtilityFunctions } from 'src/app/helpers/utility-functions';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';

export class ClientCornerMapHelper {
  private constructor() {}

  static constructSameCoordsArray(
    projects: Project[],
    soldRentedFilter = false
  ): ProjectGroup[] {
    if (!CollectionHelper.isPopulated(projects)) {
      return [];
    }
    const toReturn: ProjectGroup[] = [];
    const coordsMap = new Map<number, ProjectGroup>();
    projects.forEach((p) => {
      const baseProject = ProjectHelper.getBaseProject(p);
      if (
        baseProject != null &&
        baseProject.projectPlacement != null &&
        baseProject.projectPlacement.address != null &&
        baseProject.projectPlacement.address.coordinates != null
      ) {
        const key =
          baseProject.projectPlacement.address.coordinates.lat * 1000 +
          baseProject.projectPlacement.address.coordinates.lng;
        let group = coordsMap.get(key);
        if (group == null) {
          group = {
            lat: baseProject.projectPlacement.address.coordinates.lat,
            lng: baseProject.projectPlacement.address.coordinates.lng,
            projects: [],
          };
          coordsMap.set(key, group);
        }
        group.projects.push(p);
      }
    });
    coordsMap.forEach((group) => {
      group.markerColor = soldRentedFilter
        ? MARKER_COLORS.YELLOW_MARKER
        : this.calculateGroupMarker(group);
      toReturn.push(group);
    });
    return toReturn;
  }

  private static calculateGroupMarker(projectGroup: ProjectGroup): string {
    let intermediate: string = null;
    for (const project of projectGroup.projects) {
      intermediate = this.calculateProjectMarker(project);
      if (intermediate !== MARKER_COLORS.GREEN_MARKER) {
        return intermediate;
      }
    }
    return intermediate;
  }

  private static calculateProjectMarker(project: Project): string {
    if (!CollectionHelper.isPopulated(project.subProjects)) {
      return MARKER_COLORS.GREEN_MARKER;
    }
    for (const subProject of project.subProjects) {
      if (
        !ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(subProject.status)
      ) {
        switch (subProject.type) {
          case 'BaseProject':
            return MARKER_COLORS.GRAY_MARKER;
          case 'Adjustment':
            return MARKER_COLORS.BLUE_MARKER;
          case 'SoldRented':
            return MARKER_COLORS.YELLOW_MARKER;
          case 'Repo':
            return MARKER_COLORS.RED_MARKER;
          default:
            return MARKER_COLORS.GRAY_MARKER;
        }
      }
    }
    return MARKER_COLORS.GREEN_MARKER;
  }

  static getMostRecentSubProject(p: Project): SubProject {
    if (p == null || !CollectionHelper.isPopulated(p.subProjects)) {
      return null;
    }
    return CollectionHelper.findMaxWithCompare(
      p.subProjects,
      this.compareSubProjectRecency
    );
  }

  static getMostRecentSubProjectWithAssets(p: Project): SubProject {
    if (p == null || !CollectionHelper.isPopulated(p.subProjects)) {
      return null;
    }

    let toReturn = null;
    for (const subProject of p.subProjects) {
      if (this.subProjectHasAssets(p, subProject)) {
        toReturn =
          toReturn == null
            ? subProject
            : UtilityFunctions.max(
                toReturn,
                subProject,
                this.compareSubProjectRecency
              );
      }
    }

    toReturn =
      toReturn == null ? this.getBaseProjectWithSitePhotos(p) : toReturn;

    return toReturn;
  }

  static compareSubProjectRecency(s1: SubProject, s2: SubProject): number {
    if (s1 == null && s2 == null) {
      return 0;
    }
    if (s1 == null) {
      return -1;
    }
    if (s2 == null) {
      return 1;
    }
    if (s1.currentCreatedAt == null || s2.currentCreatedAt == null) {
      return s1.id - s2.id;
    }
    const momentDiff =
      s1.currentCreatedAt.getTime() - s2.currentCreatedAt.getTime();
    return momentDiff === 0 ? s1.id - s2.id : momentDiff;
  }

  private static subProjectHasAssets(
    project: Project,
    subProject: SubProject
  ): boolean {
    return subProject.type === 'BaseProject'
      ? this.baseProjectHasAssets(project)
      : this.adjustmentHasAssets(subProject);
  }

  private static baseProjectHasAssets(project: Project): boolean {
    if (!CollectionHelper.isPopulated(project.products)) {
      return false;
    }
    return project.products.some(
      (p) =>
        CollectionHelper.isPopulated(p.productAssets) &&
        p.productAssets.some(
          (pa) => !pa.archived && pa.formSection === 'execution'
        )
    );
  }

  private static adjustmentHasAssets(subProject: SubProject): boolean {
    return (
      subProject.projectPlacement != null &&
      CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      ) &&
      subProject.projectPlacement.projectPlacementAssets.some(
        (pa) => !pa.archived && pa.formSection === 'adjustmentPlacementSuccess'
      )
    );
  }

  private static getBaseProjectWithSitePhotos(project: Project): BaseProject {
    const toReturn = project.subProjects.find(
      (sp) =>
        sp.type === 'BaseProject' &&
        sp.projectPlacement != null &&
        CollectionHelper.isPopulated(
          sp.projectPlacement.projectPlacementAssets
        ) &&
        sp.projectPlacement.projectPlacementAssets.some(
          (pa) => pa.formSection === 'sitePhotos'
        )
    );
    return toReturn == null ? null : <BaseProject>toReturn;
  }

  static getLatestProjectAssetUrl(
    project: Project,
    subProject: SubProject
  ): string {
    return subProject.type === 'BaseProject'
      ? this.getLatestBaseProjectAssetUrl(project, subProject)
      : this.getLatestAdjustmentAssetUrl(subProject);
  }

  private static getLatestBaseProjectAssetUrl(
    project: Project,
    subProject: SubProject
  ): string {
    for (const product of project.products) {
      const productAsset = CollectionHelper.isPopulated(product.productAssets)
        ? product.productAssets.find(
            (pa) => !pa.archived && pa.formSection === 'execution'
          )
        : null;
      if (productAsset != null) {
        return `/api/assets/${productAsset.asset.uuid}/219`;
      }
    }
    if (
      subProject != null &&
      subProject.projectPlacement != null &&
      CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      )
    ) {
      const sitePhotoAsset =
        subProject.projectPlacement.projectPlacementAssets.find(
          (pa) => !pa.archived && pa.formSection === 'sitePhotos'
        );
      if (sitePhotoAsset != null) {
        return `/api/assets/${sitePhotoAsset.asset.uuid}/219`;
      }
    }
    return null;
  }

  private static getLatestAdjustmentAssetUrl(subProject: SubProject): string {
    const productAsset =
      subProject.projectPlacement.projectPlacementAssets.find(
        (pa) => !pa.archived && pa.formSection === 'adjustmentPlacementSuccess'
      );
    return productAsset == null
      ? null
      : `/api/assets/${productAsset.asset.uuid}/219`;
  }
}

export interface ProjectGroup {
  lat: number;
  lng: number;
  projects: Project[];
  markerColor?: string;
}
